// ATE-CR-117
import { useEffect, useId, useRef, useState } from "react";

import { addCredit } from "@/api/credits.api";
import { useToast } from "@/components/common/useToast";

interface Props {
  readonly tenantId: string;
}

// ATE-CR-117
export function RefundsTab({ tenantId }: Props): JSX.Element {
  const { toast } = useToast();
  const resourceId = useId();
  const amountId = useId();
  const noteId = useId();

  const [resource, setResource] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  // ATE-CR-117 — abort in-flight POST on unmount to prevent setState-after-unmount.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const parsedAmount = Number(amount);
    if (!resource.trim() || !parsedAmount || isNaN(parsedAmount)) return;

    setSubmitting(true);
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    try {
      // ATE-CR-117 — task 11 FIX: tenant id is now sent via X-Tenant-ID header,
      // body shape matches backend AddCreditsRequest (no tenant_id field).
      await addCredit(
        tenantId,
        {
          resource: resource.trim(),
          amount: parsedAmount,
          note: note.trim() || undefined,
        },
        abortRef.current.signal,
      );
      if (abortRef.current.signal.aborted) return;
      toast("success", `Added ${parsedAmount} credits to ${resource.trim()}`);
      // Reset form
      setResource("");
      setAmount("");
      setNote("");
    } catch (err) {
      if (abortRef.current.signal.aborted) return;
      const errMsg = err instanceof Error ? err.message : "Unknown error";
      toast("error", `Failed to add credit: ${errMsg}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="refunds-tab">
      <h2 className="refunds-tab__heading">Add Credits</h2>
      <form className="refunds-tab__form" onSubmit={(e) => void handleSubmit(e)}>
        <div className="refunds-tab__field">
          <label htmlFor={resourceId} className="refunds-tab__label">
            Resource
          </label>
          <input
            id={resourceId}
            type="text"
            className="refunds-tab__input"
            value={resource}
            onChange={(e) => setResource(e.target.value)}
            required
            disabled={submitting}
            placeholder="e.g. image_gen"
          />
        </div>

        <div className="refunds-tab__field">
          <label htmlFor={amountId} className="refunds-tab__label">
            Amount
          </label>
          <input
            id={amountId}
            type="number"
            className="refunds-tab__input"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            min={1}
            disabled={submitting}
            placeholder="e.g. 100"
          />
        </div>

        <div className="refunds-tab__field">
          <label htmlFor={noteId} className="refunds-tab__label">
            Note
          </label>
          <textarea
            id={noteId}
            className="refunds-tab__textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={submitting}
            rows={3}
            placeholder="Reason for credit addition"
          />
        </div>

        <button
          type="submit"
          className="btn btn--primary"
          disabled={submitting}
        >
          {submitting ? "Adding…" : "Add Credit"}
        </button>
      </form>
    </div>
  );
}
